import React from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  Award,
  Users,
  Target,
  TrendingUp,
  BookOpen,
  Shield,
  Calendar,
  AlertTriangle,
  Layers,
  Settings,
  Activity,
  Eye,
  Wrench
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { UpcomingTrainingsSection } from '../components/UpcomingTrainingsSection';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const FMEATraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "FMEA Training | Failure Mode and Effects Analysis Course",
    "description": "Enroll in professional FMEA Training to master Failure Mode and Effects Analysis. Learn DFMEA, PFMEA, SFMEA & risk prevention techniques.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy"
    },
    "coursePrerequisites": "Basic quality management knowledge recommended",
    "educationalLevel": "Intermediate to Advanced",
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "In-person",
      "duration": "2-3 days"
    }
  };

  const fmeaTypes = [
    {
      id: 'dfmea',
      title: 'Design FMEA (DFMEA)',
      icon: Layers,
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      borderColor: 'border-blue-200',
      description: 'The objective of DFMEA is to evaluate design risks early, before products move into production, when changes are less costly and easier to implement.',
      bulletPoints: [
        'Analyze potential design weaknesses',
        'Evaluate safety and reliability concerns',
        'Identify critical characteristics',
        'Prevent design-related failures',
        'Improve product robustness'
      ],
      industries: 'DFMEA is widely used in industries such as automotive, aerospace, electronics, and medical devices to improve design robustness and customer safety.'
    },
    {
      id: 'pfmea',
      title: 'Process FMEA (PFMEA)',
      icon: Settings,
      color: 'green',
      gradient: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      borderColor: 'border-green-200',
      description: 'It is conducted after the design is finalized and before full-scale production begins. PFMEA helps identify process-related risks that could lead to defects, rework, or customer complaints.',
      bulletPoints: [
        'Assess manufacturing process risks',
        'Identify process control needs',
        'Prevent process-related defects',
        'Optimize process parameters',
        'Ensure process capability'
      ],
      industries: 'PFMEA is critical for ensuring stable and capable production processes. Implementing PFMEA enables organizations to prevent process failures, improve operational efficiency, and ensure consistent product quality.'
    },
    {
      id: 'sfmea',
      title: 'System FMEA (SFMEA)',
      icon: Activity,
      color: 'purple',
      gradient: 'from-purple-500 to-indigo-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      borderColor: 'border-purple-200',
      description: 'SFMEA is especially important for complex systems where failures can occur due to interface issues rather than individual component defects.',
      bulletPoints: [
        'Analyze system-level interactions',
        'Evaluate interface compatibility',
        'Identify subsystem dependencies',
        'Assess system integration risks',
        'Ensure overall system reliability'
      ],
      industries: 'This type of FMEA is commonly used in aerospace, automotive, energy, and large-scale industrial systems. SFMEA helps organizations identify system-level risks early, ensuring that all components work together reliably and safely.'
    },
    {
      id: 'fmea-msr',
      title: 'FMEA-MSR (Monitoring & System Response)',
      icon: Eye,
      color: 'orange',
      gradient: 'from-orange-500 to-red-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      borderColor: 'border-orange-200',
      description: 'This type of FMEA ensures that when failures occur, systems can detect them early and respond appropriately to minimize impact.',
      bulletPoints: [
        'Design failure detection systems',
        'Define system response strategies',
        'Implement monitoring mechanisms',
        'Ensure fail-safe operations',
        'Minimize failure impact'
      ],
      industries: 'FMEA-MSR is particularly important in safety-critical applications such as automotive safety systems, industrial automation, and medical devices. By implementing FMEA-MSR, organizations enhance safety, reduce risk exposure, and ensure compliance with industry safety standards.'
    }
  ];

  const importanceReasons = [
    'Early identification of potential failures',
    'Reduction in rework, scrap, and warranty costs',
    'Improved customer satisfaction and trust',
    'Compliance with quality standards and customer requirements',
    'Enhanced cross-functional collaboration'
  ];

  const courseModules = [
    {
      title: 'FMEA Fundamentals',
      icon: BookOpen,
      topics: [
        'Introduction to risk-based thinking',
        'FMEA history and evolution',
        'Types of FMEA and applications',
        'FMEA standards (AIAG-VDA)',
        'Risk assessment methodology'
      ]
    },
    {
      title: 'FMEA Process & Structure',
      icon: Target,
      topics: [
        'Seven-step FMEA approach',
        'Structure analysis and function analysis',
        'Failure chain development',
        'Risk evaluation and prioritization',
        'Action plan development'
      ]
    },
    {
      title: 'DFMEA & PFMEA Application',
      icon: Wrench,
      topics: [
        'Design FMEA methodology',
        'Process FMEA methodology',
        'Severity, Occurrence, Detection ratings',
        'Risk Priority Number (RPN) calculation',
        'Action Priority (AP) determination'
      ]
    },
    {
      title: 'Advanced FMEA Techniques',
      icon: TrendingUp,
      topics: [
        'System FMEA implementation',
        'FMEA-MSR for safety systems',
        'Integration with other tools (Control Plans, APQP)',
        'FMEA software and automation',
        'Continuous improvement practices'
      ]
    }
  ];

  const trainingBenefits = [
    {
      icon: Shield,
      title: 'Proactive Risk Prevention',
      description: 'Move from reactive problem-solving to proactive risk identification and prevention strategies.'
    },
    {
      icon: TrendingUp,
      title: 'Cost Reduction',
      description: 'Significantly reduce costs associated with rework, scrap, warranty claims, and recalls.'
    },
    {
      icon: Users,
      title: 'Enhanced Collaboration',
      description: 'Foster cross-functional teamwork between design, manufacturing, quality, and engineering teams.'
    },
    {
      icon: Award,
      title: 'Industry Recognition',
      description: 'Gain credentials recognized across automotive, aerospace, medical devices, and manufacturing industries.'
    },
    {
      icon: CheckCircle,
      title: 'Standards Compliance',
      description: 'Meet requirements of ISO 9001, IATF 16949, AS9100, and other quality management standards.'
    },
    {
      icon: Target,
      title: 'Quality Excellence',
      description: 'Improve product quality, process reliability, and overall customer satisfaction.'
    }
  ];

  const whyDifferentTypesMatter = [
    'Prevent failures proactively',
    'Improve product and process reliability',
    'Enhance safety and regulatory compliance',
    'Reduce costs associated with defects and recalls',
    'Strengthen customer confidence'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="FMEA Training | Failure Mode and Effects Analysis Course"
        description="Enroll in professional FMEA Training to master Failure Mode and Effects Analysis. Learn DFMEA, PFMEA, SFMEA & risk prevention techniques."
        keywords="FMEA training, failure mode effects analysis, DFMEA, PFMEA, SFMEA, risk analysis, quality management, AIAG-VDA FMEA, automotive FMEA"
        canonical="/FMEA-Training"
        structuredData={structuredData}
        breadcrumbs={[
          { name: 'Training', path: '/training' },
          { name: 'FMEA Training', path: '/FMEA-Training' }
        ]}
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 via-cyan-600 to-teal-600 text-white py-20 mt-20">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-sm mb-6 font-semibold">
              <AlertTriangle className="h-5 w-5" />
              PROACTIVE RISK MANAGEMENT
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              FMEA Training: Build Proactive Risk Management Skills for Quality Excellence
            </h1>
            <p className="text-xl md:text-2xl text-blue-100 mb-8 max-w-4xl mx-auto">
              Master Failure Mode and Effects Analysis to prevent failures before they occur. Learn DFMEA, PFMEA, SFMEA & risk prevention techniques from industry experts.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-lg">
              <div className="flex items-center gap-2">
                <Calendar className="h-6 w-6" />
                <span>2-3 Days Training</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="h-6 w-6" />
                <span>Professional Certificate</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-6 w-6" />
                <span>Practical Workshops</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Introduction Section */}
        <section className="mb-16">
          <Card>
            <CardContent className="p-8">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                In today's competitive manufacturing and service environments, preventing failures is far more cost-effective than correcting them after they occur. Organizations across industries are increasingly adopting Failure Mode and Effects Analysis (FMEA) training to identify risks early, improve product quality, and enhance process reliability. FMEA training equips professionals with the knowledge and practical skills needed to apply this powerful risk-analysis tool effectively.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Whether you are part of product development, quality assurance, manufacturing, or continuous improvement, FMEA training helps you move from reactive problem-solving to proactive risk prevention.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Importance of FMEA Training */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">
              Importance of FMEA Training
            </h2>
            <Card className="bg-red-50 border-red-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Organizations face increasing pressure to deliver high-quality, safe, and reliable products while meeting customer and regulatory requirements. FMEA training provides teams with a systematic approach to risk assessment and prevention.
                </p>
                <h3 className="text-xl font-bold mb-4 text-gray-900">Key reasons why FMEA training is essential include:</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {importanceReasons.map((reason, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-red-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700">{reason}</span>
                    </div>
                  ))}
                </div>
                <p className="text-lg text-gray-700 mt-6 font-semibold">
                  By investing in FMEA training, organizations build a culture of prevention rather than correction.
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Types of FMEA Covered */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold mb-8 text-gray-900">
              Types of FMEA Covered
            </h3>
            <div className="space-y-8">
              {fmeaTypes.map((type, index) => {
                const IconComponent = type.icon;
                return (
                  <motion.div
                    key={type.id}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Card className={`border-2 ${type.borderColor} overflow-hidden hover:shadow-lg transition-shadow`}>
                      <div className={`bg-gradient-to-r ${type.gradient} p-6 text-white`}>
                        <div className="flex items-center gap-4">
                          <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl">
                            <IconComponent className="h-8 w-8" />
                          </div>
                          <h4 className="text-2xl font-bold">{type.title}</h4>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <p className="text-gray-700 text-lg mb-6">{type.description}</p>
                        <div className={`${type.bgColor} p-6 rounded-lg mb-6`}>
                          <h5 className={`font-bold text-lg ${type.textColor} mb-4`}>Key Focus Areas:</h5>
                          <ul className="space-y-3">
                            {type.bulletPoints.map((point, idx) => (
                              <li key={idx} className="flex items-start gap-3">
                                <CheckCircle className={`h-5 w-5 ${type.textColor} flex-shrink-0 mt-0.5`} />
                                <span className="text-gray-700">{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        <p className="text-gray-600 italic">{type.industries}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </section>

        {/* FMEA Training Course Modules */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900">
              FMEA Training Course Modules
            </h4>
            <div className="grid md:grid-cols-2 gap-6">
              {courseModules.map((module, index) => {
                const IconComponent = module.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="bg-gradient-to-br from-red-100 to-orange-100 p-3 rounded-lg">
                          <IconComponent className="h-8 w-8 text-red-600" />
                        </div>
                        <h5 className="text-xl font-bold text-gray-900">{module.title}</h5>
                      </div>
                      <ul className="space-y-2">
                        {module.topics.map((topic, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="text-red-600 mt-1">•</span>
                            <span className="text-gray-700">{topic}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        </section>

        {/* FMEA Training Benefits */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-8 text-gray-900">
              FMEA Training Benefits
            </h4>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trainingBenefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="hover:shadow-lg transition-shadow group">
                    <CardContent className="p-6">
                      <div className="bg-gradient-to-br from-red-100 to-orange-100 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <IconComponent className="h-7 w-7 text-red-600" />
                      </div>
                      <h5 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h5>
                      <p className="text-gray-600">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </motion.div>
        </section>

        {/* Why Different Types Matter */}
        <section className="mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-6 text-gray-900">
              Why Understanding Different Types of FMEA Matters
            </h4>
            <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
              <CardContent className="p-8">
                <p className="text-lg text-gray-700 mb-6">
                  Each FMEA type addresses risk at a different stage of the product or process lifecycle. Applying the right type of FMEA at the right time helps organizations:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  {whyDifferentTypesMatter.map((item, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <CheckCircle className="h-6 w-6 text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>

        {/* Related Courses */}
        <RelatedCoursesSection currentSlug="iatf-16949" slugs={['iatf-16949', 'iso-9001', 'iso-14001']} heading="Related ISO Training Courses" />

        {/* CTA Section */}
        <section>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Card className="bg-gradient-to-r from-red-600 to-orange-600 text-white">
              <CardContent className="p-12 text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Ready to Master FMEA and Build a Culture of Prevention?
                </h2>
                <p className="text-xl text-red-100 mb-8 max-w-3xl mx-auto">
                  Join industry professionals who have transformed their risk management approach with our comprehensive FMEA training program.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <Button
                    onClick={() => navigate('/training-calendar')}
                    className="bg-white text-red-600 hover:bg-gray-100 px-8 py-6 text-lg"
                    size="lg"
                  >
                    <Calendar className="h-5 w-5 mr-2" />
                    View Training Schedule
                  </Button>
                  <Button
                    onClick={() => navigate('/contact')}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-red-600 px-8 py-6 text-lg"
                    size="lg"
                  >
                    Request More Information
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </section>
      </div>

      {/* Upcoming Trainings Section */}
      <UpcomingTrainingsSection
        pageSlug="fmea-training"
        title="Upcoming FMEA Training Sessions"
      />
    </div>
  );
};
